import React, { useEffect, useState } from 'react';
import {
  Badge, Button, DataTable, IdCell, KV, Modal, PageHead, SearchBox, Select,
  StatCard, Tabs, dateStr, inr, num, titleCase,
} from '../components/ui';
import useList from '../hooks/useList';
import { insuranceApi } from '../api';
import { useToast } from '../context/ToastContext';

const Insurance = () => {
  const toast = useToast();
  const [tab, setTab] = useState('policies');
  const [stats, setStats] = useState(null);
  const [providers, setProviders] = useState([]);
  const [detail, setDetail] = useState(null);
  const [statusEdit, setStatusEdit] = useState(null);
  const [newStatus, setNewStatus] = useState('active');
  const [busy, setBusy] = useState(false);

  const policies = useList(insuranceApi.policies, { status: '', type: '', search: '' });
  const claims = useList(insuranceApi.claims, { status: '' }, { auto: false });

  useEffect(() => {
    insuranceApi.stats().then(res => setStats(res.data)).catch(() => {});
    insuranceApi.providers().then(res => setProviders(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === 'claims') claims.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const saveStatus = async () => {
    setBusy(true);
    try {
      await insuranceApi.setPolicyStatus(statusEdit._id, { status: newStatus });
      toast.success(`Policy ${newStatus}`);
      setStatusEdit(null);
      setDetail(null);
      policies.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const policyColumns = [
    {
      key: 'policyNumber',
      label: 'Policy',
      render: row => (
        <>
          <div className="cell-strong mono">{row.policyNumber}</div>
          <div className="cell-muted">{dateStr(row.createdAt)}</div>
        </>
      ),
    },
    { key: 'type', label: 'Cover', render: row => <Badge status={row.type} tone="brand" dot={false}>{titleCase(row.type)}</Badge> },
    { key: 'holder', label: 'Policy holder', render: row => <IdCell image={row.holder?.logo} name={row.holder?.name} sub={row.holder?.code} size="sm" /> },
    { key: 'insurer', label: 'Insurer', render: row => row.insurer?.name || '—' },
    { key: 'coverageAmount', label: 'Cover', align: 'right', render: row => <strong>{inr(row.coverageAmount, true)}</strong> },
    { key: 'premium', label: 'Premium', align: 'right', render: row => inr(row.premium) },
    { key: 'validTill', label: 'Valid till', render: row => <span className="text-sm">{dateStr(row.validTill)}</span> },
    { key: 'status', label: 'Status', render: row => <Badge status={row.status} /> },
  ];

  const claimColumns = [
    { key: 'claimNumber', label: 'Claim', render: row => <span className="mono cell-strong">{row.claimNumber}</span> },
    { key: 'policy', label: 'Policy', render: row => <span className="mono text-sm">{row.policy?.policyNumber || '—'}</span> },
    { key: 'raisedBy', label: 'Claimant', render: row => row.raisedBy?.name || '—' },
    { key: 'order', label: 'Order', render: row => <span className="mono text-sm">{row.order?.orderNumber || '—'}</span> },
    { key: 'claimAmount', label: 'Claimed', align: 'right', render: row => <strong>{inr(row.claimAmount)}</strong> },
    { key: 'status', label: 'Status', render: row => <Badge status={row.status} /> },
  ];

  return (
    <>
      <PageHead title="Insurance" subtitle="Shipment, payment and product-damage cover issued on the network." />

      {stats && (
        <div className="stat-grid stagger mb-2">
          <StatCard accent label="Active coverage" value={inr(stats.activeCoverage, true)} icon="🛡" foot={`${num(stats.totalPolicies)} policies`} />
          <StatCard label="Premium collected" value={inr(stats.premiumCollected, true)} icon="₹" />
          <StatCard label="Active policies" value={num(stats.policies.active)} icon="✓" />
          <StatCard label="Quotes pending" value={num(stats.policies.quoted)} icon="⏳" />
          <StatCard label="Claims pending" value={num(stats.claimsPending)} icon="⚠" onClick={() => setTab('claims')} />
          <StatCard label="Claims settled" value={num(stats.claimsSettled)} icon="✓" foot={inr(stats.settlementAmount, true)} />
        </div>
      )}

      <Tabs
        tabs={[
          { value: 'policies', label: 'Policies' },
          { value: 'claims', label: 'Insured claims' },
          { value: 'providers', label: `Providers (${providers.length})` },
        ]}
        value={tab}
        onChange={setTab}
      />

      {tab === 'policies' && (
        <>
          <div className="filters">
            <SearchBox value={policies.filters.search} onChange={v => policies.setFilter('search', v)} placeholder="Search policy number…" />
            <select className="select" style={{ width: 170 }} value={policies.filters.status} onChange={e => policies.setFilter('status', e.target.value)}>
              <option value="">Any status</option>
              <option value="quoted">Quoted</option>
              <option value="active">Active</option>
              <option value="claimed">Claimed</option>
              <option value="expired">Expired</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select className="select" style={{ width: 200 }} value={policies.filters.type} onChange={e => policies.setFilter('type', e.target.value)}>
              <option value="">All cover types</option>
              <option value="shipment">Shipment</option>
              <option value="payment">Payment</option>
              <option value="product_damage">Product damage</option>
            </select>
          </div>
          <DataTable
            columns={policyColumns}
            rows={policies.rows}
            loading={policies.loading}
            meta={policies.meta}
            onPage={policies.setPage}
            onRowClick={async row => {
              try {
                setDetail((await insuranceApi.policy(row._id)).data);
              } catch (err) {
                toast.error(err.message);
              }
            }}
          />
        </>
      )}

      {tab === 'claims' && (
        <>
          <div className="filters">
            <select className="select" style={{ width: 190 }} value={claims.filters.status} onChange={e => claims.setFilter('status', e.target.value)}>
              <option value="">Any status</option>
              <option value="raised">Raised</option>
              <option value="under_review">Under review</option>
              <option value="verified">Verified</option>
              <option value="approved">Approved</option>
              <option value="settled">Settled</option>
            </select>
          </div>
          <DataTable columns={claimColumns} rows={claims.rows} loading={claims.loading} meta={claims.meta} onPage={claims.setPage} />
        </>
      )}

      {tab === 'providers' && (
        <div className="grid-2 stagger">
          {providers.map(p => (
            <div className="card" key={p._id}>
              <div className="card-body">
                <IdCell image={p.logo} name={p.name} sub={p.code} size="lg" />
                <p className="text-sm text-muted mt-2">{p.description || 'No description provided.'}</p>
              </div>
            </div>
          ))}
          {providers.length === 0 && (
            <p className="text-muted">No verified insurance partners have joined the network yet.</p>
          )}
        </div>
      )}

      <Modal
        open={!!detail}
        size="wide"
        title={detail?.policyNumber}
        subtitle={detail ? titleCase(detail.type) : ''}
        onClose={() => setDetail(null)}
        footer={
          detail && (
            <Button onClick={() => { setStatusEdit(detail); setNewStatus(detail.status); }}>Change status</Button>
          )
        }
      >
        {detail && (
          <>
            <div className="row mb-2"><Badge status={detail.status} /></div>
            <KV
              items={[
                { label: 'Insurer', value: detail.insurer?.name },
                { label: 'Policy holder', value: `${detail.holder?.name} (${detail.holder?.code})` },
                { label: 'Coverage', value: <strong>{inr(detail.coverageAmount)}</strong> },
                { label: 'Premium', value: inr(detail.premium) },
                { label: 'Deductible', value: inr(detail.deductible) },
                { label: 'Valid', value: `${dateStr(detail.validFrom)} → ${dateStr(detail.validTill)}` },
                detail.order && { label: 'Order', value: `${detail.order.orderNumber} (${inr(detail.order.grandTotal)})` },
                detail.shipment && { label: 'Shipment', value: detail.shipment.shipmentNumber },
                { label: 'Claimed so far', value: inr(detail.claimedAmount) },
              ]}
            />

            {detail.coverageDetails && (
              <>
                <div className="divider" />
                <div className="card-title mb-2">What's covered</div>
                <p className="text-sm text-muted">{detail.coverageDetails}</p>
              </>
            )}

            {(detail.exclusions || []).length > 0 && (
              <>
                <div className="divider" />
                <div className="card-title mb-2">Exclusions</div>
                <ul className="text-sm text-muted">
                  {detail.exclusions.map(item => <li key={item}>{item}</li>)}
                </ul>
              </>
            )}

            {(detail.claims || []).length > 0 && (
              <>
                <div className="divider" />
                <div className="card-title mb-2">Claims against this policy</div>
                {detail.claims.map(claim => (
                  <div className="row-between text-sm" key={claim._id} style={{ padding: '5px 0' }}>
                    <span className="mono">{claim.claimNumber}</span>
                    <span>{inr(claim.claimAmount)}</span>
                    <Badge status={claim.status} />
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </Modal>

      <Modal
        open={!!statusEdit}
        title="Change policy status"
        subtitle={statusEdit?.policyNumber}
        onClose={() => setStatusEdit(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setStatusEdit(null)}>Cancel</Button>
            <Button loading={busy} onClick={saveStatus}>Save</Button>
          </>
        }
      >
        <Select
          label="Status" value={newStatus} onChange={e => setNewStatus(e.target.value)}
          options={[
            { value: 'quoted', label: 'Quoted — awaiting acceptance' },
            { value: 'active', label: 'Active — cover in force' },
            { value: 'claimed', label: 'Claimed' },
            { value: 'expired', label: 'Expired' },
            { value: 'cancelled', label: 'Cancelled' },
          ]}
          hint="Activating a policy marks its linked order as insured and notifies the holder."
        />
      </Modal>
    </>
  );
};

export default Insurance;
